import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { pdfStyles } from './PDFStyles';
import { preprocessTextForPDF, registerEmojiSource } from './EmojiProcessor';
import { HeaderSection } from './PDFSections/HeaderSection';
import { FooterSection } from './PDFSections/FooterSection';
import { ContentSection } from './PDFSections/ContentSection';
import { MetricsSection } from './PDFSections/MetricsSection';
import { SummarySection } from './PDFSections/SummarySection';

// Register emoji source on import
registerEmojiSource();

interface PDFDocumentProps {
  simulation: any;
  displayResults: any;
  formData: {
    content: string;
    platform: string;
    sender: string;
    sector: string;
  };
  forecast: any;
  reputationInfo: {
    level: string;
    color: string;
  };
  uiScore: number;
  chartImages?: {
    sentimentChart?: string;
    gaugeChart?: string;
  };
  user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

// Helper function to capitalize first letter
const capitalizeFirstLetter = (string?: string | null) => {
  if (!string || typeof string !== 'string') return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const PDFDocument: React.FC<PDFDocumentProps> = ({
  simulation,
  displayResults,
  formData,
  forecast,
  reputationInfo,
  uiScore,
  chartImages,
  user
}) => {
  // Split reactions into chunks that can fit on a page
  const reactions = displayResults?.data?.simulation || [];
  // Filter out null/undefined reactions before chunking
  const validReactions = reactions.filter((reaction: any) => reaction && reaction.text);
  const reactionsPerPage = 11; // Reduced from 15 to prevent page overflow
  const reactionChunks = [];
  
  for (let i = 0; i < validReactions.length; i += reactionsPerPage) {
    reactionChunks.push(validReactions.slice(i, i + reactionsPerPage));
  }

  const renderReactionChunk = (chunk: any[], pageIndex: number) => {
    // Only create a page if the chunk has valid reactions
    const validChunkReactions = chunk.filter((reaction: any) => reaction && reaction.text && reaction.text.trim());
    
    if (validChunkReactions.length === 0) return null;
    
    return (
      <Page key={`reactions-${pageIndex}`} size="A4" style={pdfStyles.page}>
        <HeaderSection forecast={forecast} formData={formData} simulation={simulation} user={user} />
        <View style={pdfStyles.reactionsPageContainer}>
          <View style={pdfStyles.card}>
            <Text style={pdfStyles.cardTitle}>
              Sample Reactions {reactionChunks.length > 1 ? `(${pageIndex + 1}/${reactionChunks.length})` : ''}
            </Text>
            <View style={pdfStyles.reactionsContainer}>
              {validChunkReactions.map((reaction: any, index: number) => {
                const getSentimentStyle = () => {
                  const sentimentValue = reaction.sentiment || reaction.tag;
                  
                  // Check both emoji tags and text values
                  if (sentimentValue === 'positive' || sentimentValue === '👍') {
                    return {
                      ...pdfStyles.reactionItem,
                      ...pdfStyles.positiveReaction,
                      ...pdfStyles.positiveBackground
                    };
                  }
                  if (sentimentValue === 'neutral' || sentimentValue === '😐') {
                    return {
                      ...pdfStyles.reactionItem,
                      ...pdfStyles.neutralReaction,
                      ...pdfStyles.neutralBackground
                    };
                  }
                  if (sentimentValue === 'negative' || sentimentValue === '👎') {
                    return {
                      ...pdfStyles.reactionItem,
                      ...pdfStyles.negativeReaction,
                      ...pdfStyles.negativeBackground
                    };
                  }
                  
                  return {
                    ...pdfStyles.reactionItem,
                    ...pdfStyles.defaultBackground
                  };
                };

                // Only show meta information if we have valid sentiment and reason
                const sentimentValue = reaction.sentiment || reaction.tag;
                const hasValidMeta = sentimentValue && 
                                   sentimentValue !== 'unknown' && 
                                   reaction.reason && 
                                   reaction.reason !== 'No reason provided';

                // Convert emoji to text for display
                const getSentimentText = (value: string) => {
                  if (value === '👍') return 'Positive';
                  if (value === '😐') return 'Neutral';
                  if (value === '👎') return 'Negative';
                  return capitalizeFirstLetter(value);
                };

                return (
                  <View 
                    key={`reaction-${pageIndex}-${index}`} 
                    style={getSentimentStyle()}
                  >
                    <Text style={pdfStyles.reactionText}>{preprocessTextForPDF(reaction.text)}</Text>
                    {hasValidMeta && (
                      <Text style={pdfStyles.reactionMeta}>
                        {getSentimentText(sentimentValue)} • {preprocessTextForPDF(reaction.reason)}
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>
          </View>
        </View>
        <FooterSection pageNumber={pageIndex + 4} />
      </Page>
    );
  };

  return (
    <Document>
      {/* Page 1 - Content Preview and Metrics */}
      <Page size="A4" style={pdfStyles.page}>
        <HeaderSection forecast={forecast} formData={formData} simulation={simulation} user={user} />
        <ContentSection formData={formData} simulation={simulation} forecast={forecast} />
        <MetricsSection forecast={forecast} reputationInfo={reputationInfo} uiScore={uiScore} />
        <FooterSection pageNumber={1} />
      </Page>

      {/* Page 2 - Detailed Analysis (Part 1) */}
      <Page size="A4" style={pdfStyles.page}>
        <HeaderSection forecast={forecast} formData={formData} simulation={simulation} user={user} />
        <SummarySection 
          forecast={forecast} 
          displayResults={displayResults} 
          isFirstPage={true}
        />
        <FooterSection pageNumber={2} />
      </Page>

      {/* Page 3 - Detailed Analysis (Part 2) */}
      <Page size="A4" style={pdfStyles.page}>
        <HeaderSection forecast={forecast} formData={formData} simulation={simulation} user={user} />
        <SummarySection 
          forecast={forecast} 
          displayResults={displayResults} 
          isSecondPage={true}
        />
        <FooterSection pageNumber={3} />
      </Page>

      {/* Reactions Pages - Each chunk on its own page to avoid breaks */}
      {reactionChunks.length > 0 && reactionChunks.map((chunk, pageIndex) => 
        renderReactionChunk(chunk, pageIndex)
      )}

      {/* Fallback: If no reactions, still show the reactions section */}
      {validReactions.length === 0 && (
        <Page size="A4" style={pdfStyles.page}>
          <HeaderSection forecast={forecast} formData={formData} simulation={simulation} user={user} />
          <View style={pdfStyles.reactionsPageContainer}>
            <View style={pdfStyles.card}>
              <Text style={pdfStyles.cardTitle}>Sample Reactions</Text>
              <View style={pdfStyles.reactionsContainer}>
                <View style={pdfStyles.reactionItem}>
                  <Text style={pdfStyles.reactionText}>No sample reactions available</Text>
                </View>
              </View>
            </View>
          </View>
          <FooterSection pageNumber={4} />
        </Page>
      )}
    </Document>
  );
};